import { motion, AnimatePresence } from "motion/react";
import { useMemo } from "react";

import {
  DEFAULT_CHAT_STARTERS,
  type ChatStarterSet,
} from "@/components/chat/chatStarters";
import { MessageBubble, type MessageAttachment } from "@/components/chat/MessageBubble";
import { NotebookNoteCard } from "@/components/chat/NotebookNoteCard";
import { ThinkingBlock } from "@/components/chat/ThinkingBlock";
import { ToolCallsPanel } from "@/components/chat/ToolCallsPanel";
import type { ToolEvent } from "@/hooks/useChat";
import { useChatScroll } from "@/hooks/useChatScroll";
import { collectNotebookWrites } from "@domain/notebook-write-event";

interface Message {
  _id: string;
  role: string;
  content: string;
  kind?: "user" | "synthetic";
  pending?: boolean;
  attachments?: MessageAttachment[];
  thinking?: string;
  toolCalls?: { name: string; args: string; result?: string }[];
}

interface MessageListProps {
  isConnected: boolean;
  messages: Message[];
  streamingContent: string;
  thinkingContent: string;
  toolEvents: ToolEvent[];
  isStreaming: boolean;
  isWaitingForResponse: boolean;
  /** Prefill the composer with a starter template (empty-thread only). */
  onPickTemplate?: (prompt: string) => void;
  /**
   * Which empty-thread starter bubbles to render — the surface's strategy
   * (chat route → default general-purpose set; workflows gestation route →
   * `WORKFLOW_CHAT_STARTERS`). One implementation, injected data.
   */
  /**
   * `undefined` ⇒ the default set. `null` ⇒ render NO starter UI at all, not
   * even the intro line (onboarding-v2 §3.6): during the first-run ritual the
   * agent speaks first, and a "pick a starting point" prompt sitting under
   * its opening question competes with it.
   */
  starterSet?: ChatStarterSet | null;
  /** Older history remains unfetched — render the load-earlier affordance. */
  canLoadMore?: boolean;
  /** An older page is in flight. */
  isLoadingMore?: boolean;
  /** Request the next older page. */
  onLoadMore?: () => void;
  /**
   * Promote an agent-written note to the full-page notebook, that exact note
   * selected (`/notebook?path=…`). Injected so this component stays pure —
   * navigation belongs to the route (§4.6). Omit and the note cards still
   * render their preview, just without an Open affordance.
   */
  onOpenNote?: (path: string) => void;
}

export function MessageList({
  isConnected,
  messages,
  streamingContent,
  thinkingContent,
  toolEvents,
  isStreaming,
  isWaitingForResponse,
  onPickTemplate,
  starterSet = DEFAULT_CHAT_STARTERS,
  canLoadMore = false,
  isLoadingMore = false,
  onLoadMore,
  onOpenNote,
}: MessageListProps) {
  const { scrollRef, bottomRef, captureBeforeLoadMore } = useChatScroll({
    lastMessageId: messages[messages.length - 1]?._id,
    messageCount: messages.length,
    streamingContent,
    thinkingContent,
    toolEventCount: toolEvents.length,
    isWaitingForResponse,
  });

  // Notebook writes recognized off the TOOL-EVENT stream (§4.17). Derived once
  // per history change rather than per render: recognition JSON-parses every
  // tool call's args, and a long thread carries a lot of them.
  const notesByMessageId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof collectNotebookWrites>>();
    for (const msg of messages) {
      if (!msg.toolCalls?.length) continue;
      const notes = collectNotebookWrites(msg.toolCalls);
      if (notes.length > 0) map.set(msg._id, notes);
    }
    return map;
  }, [messages]);

  // Live half of the same seam — the note appears the instant the agent writes
  // it, from the streamed args, with no wait for the turn to persist.
  const streamingNotes = useMemo(
    () => collectNotebookWrites(toolEvents),
    [toolEvents],
  );

  // Measure before asking for more so the hook can subtract the added height
  // back out — otherwise prepending pushes the reader's position downward.
  const handleLoadMore = () => {
    captureBeforeLoadMore();
    onLoadMore?.();
  };

  if (messages.length === 0 && !isStreaming && !isWaitingForResponse) {
    if (!isConnected) {
      return (
        <div className="flex flex-1 items-center justify-center p-8">
          <p className="font-body text-sm text-black/40 dark:text-white/40">
            Initializing…
          </p>
        </div>
      );
    }
    // Explicitly suppressed (pre-onboarding, or status still loading): no
    // chips, no intro line, nothing that races the agent's opening message.
    if (starterSet === null) {
      return <div className="flex flex-1" />;
    }
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-5 p-6 text-center">
        <p className="font-body text-[14px] text-black/45 dark:text-white/45">
          {starterSet.intro}
        </p>
        <div className="grid w-full max-w-md grid-cols-1 gap-2.5 sm:grid-cols-2">
          {starterSet.starters.map((tpl, i) => {
            const Icon = tpl.icon;
            return (
              <motion.button
                key={tpl.id}
                type="button"
                onClick={() => onPickTemplate?.(tpl.prompt)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 260, damping: 22, delay: 0.05 + i * 0.06 }}
                className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-3 text-left shadow-[0_10px_24px_-18px_rgba(0,0,0,0.5)] transition-shadow hover:shadow-[0_14px_30px_-16px_rgba(0,0,0,0.45)] dark:border-white/10 dark:bg-white/5"
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tpl.tone}`}>
                  <Icon className="h-4 w-4" strokeWidth={2.2} />
                </span>
                <span className="min-w-0">
                  <span className="block font-body text-[13px] font-semibold text-black dark:text-white">
                    {tpl.label}
                  </span>
                  <span className="block truncate font-body text-[12px] text-black/45 dark:text-white/45">
                    {tpl.hint}
                  </span>
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-5">
      {canLoadMore && (
        <div className="flex justify-center pb-1">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="font-body border border-black/15 px-3 py-1.5 text-[12px] text-black/55 transition-colors hover:bg-black/5 disabled:opacity-50 dark:border-white/15 dark:text-white/55 dark:hover:bg-white/5"
          >
            {isLoadingMore ? "Loading…" : "Load earlier messages"}
          </button>
        </div>
      )}
      <AnimatePresence initial={false}>
        {messages.map((msg) => (
          <motion.div
            key={msg._id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {msg.thinking && msg.thinking.length > 0 && (
              <ThinkingBlock
                content={msg.thinking}
                isStreaming={false}
                defaultOpen={false}
              />
            )}
            {msg.toolCalls && msg.toolCalls.length > 0 && (
              <ToolCallsPanel
                tools={msg.toolCalls}
                isStreaming={false}
                defaultOpen={false}
              />
            )}
            <MessageBubble
              role={msg.role}
              content={msg.content}
              kind={msg.kind}
              pending={msg.pending}
              attachments={msg.attachments}
            />
            {notesByMessageId.get(msg._id)?.map((note) => (
              <NotebookNoteCard
                key={note.path}
                path={note.path}
                title={note.title}
                body={note.body}
                onOpen={onOpenNote}
              />
            ))}
          </motion.div>
        ))}

        {isWaitingForResponse && (
          <motion.div
            key="waiting"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="flex justify-start"
          >
            <div className="rounded-[14px_14px_14px_4px] border border-black/5 bg-white/80 px-5 py-4 backdrop-blur-sm dark:border-white/10 dark:bg-white/5">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 animate-bounce rounded-full bg-black/40 dark:bg-white/40 [animation-delay:0ms]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-black/40 dark:bg-white/40 [animation-delay:150ms]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-black/40 dark:bg-white/40 [animation-delay:300ms]" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {(isStreaming || streamingContent.length > 0) && (
        <>
          {thinkingContent.length > 0 && (
            <ThinkingBlock
              content={thinkingContent}
              isStreaming={isStreaming}
              collapseSignal={streamingContent.length > 0}
            />
          )}
          {toolEvents.length > 0 && (
            <ToolCallsPanel
              tools={toolEvents}
              isStreaming={isStreaming}
              collapseSignal={streamingContent.length > 0}
            />
          )}
          {streamingContent.length > 0 && (
            <MessageBubble
              role="assistant"
              content={streamingContent}
              isStreaming={isStreaming}
            />
          )}
          {streamingNotes.map((note) => (
            <NotebookNoteCard
              key={note.path}
              path={note.path}
              title={note.title}
              body={note.body}
              onOpen={onOpenNote}
            />
          ))}
        </>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
